<template>
    <div>

        <vue-headful title="MIC Advanced Export :: MeritEHS"/>

        <h4 class="py-3 mb-4">
            <ol class="breadcrumb font-weight-bold m-0">
                <li class="breadcrumb-item font-weight-light">
                    <router-link :to="`/hse`">
                        <i class="fa fa-shield"></i> MIC
                    </router-link>
                </li>
                <li class="breadcrumb-item active">Participation Export</li>
            </ol>
        </h4>

        <div class="row">
            <div class="col-xl-8 col">
                <div class="card mb-4">
                    <div class="card-header">
                        Export Filters
                    </div>
                    <div class="card-body">
                        <div class="form-group row">
                            <label :class="{ 'col-lg-2 col-md-3 col-sm-4 col-form-label text-sm-right': true, 'text-danger': (errors.start_date || errors.end_date)}">
                                Date Range: <i class="fa fa-asterisk text-danger" v-if="!start_date || !end_date"></i>
                            </label>
                            <div class="col-sm-8">
                                <div class="form-inline">
                                    <label class="mr-1">Start:</label>
                                    <input type="date" v-model="start_date" :class="{'form-control': true, 'is-invalid': errors.start_date}" placeholder="Start" />

                                    <span class="mx-3">to</span>

                                    <label class="mr-1">End:</label>
                                    <input type="date" v-model="end_date" :class="{'form-control': true, 'is-invalid': errors.end_date}" placeholder="End" />
                                </div>
                                <span v-if="errors.start_date" class="text-danger">
                                    <span v-for="error in errors.start_date">{{ error }}</span>
                                </span>
                                <span v-if="errors.end_date" class="text-danger">
                                    <span v-for="error in errors.end_date">{{ error }}</span>
                                </span>
                            </div>
                        </div>
                        <div class="form-group row">
                            <label :class="{ 'col-lg-2 col-md-3 col-sm-4 col-form-label text-sm-right': true, 'text-danger': errors.interval}">
                                Date Interval: <i class="fa fa-asterisk text-danger" v-if="!interval"></i>
                            </label>
                            <div class="col-sm-8">
                                <select v-model="interval" :class="{'form-control': true, 'is-invalid': errors.interval}">
                                    <option :value="null">-- Select a Date Interval --</option>
                                    <option :value="key" v-for="(value, key) in intervals">{{ value }}</option>
                                </select>
                                <small>This will group the data by the option selected.</small>
                                <span v-if="errors.interval" class="text-danger">
                                    <span v-for="error in errors.interval">{{ error }}</span>
                                </span>
                            </div>
                        </div>
                        <div class="form-group row">
                            <label :class="{ 'col-lg-2 col-md-3 col-sm-4 col-form-label text-sm-right': true, 'text-danger': errors.division_id}">Division:</label>
                            <div class="col-sm-8">
                                <select v-model="division_id" :class="{'form-control': true, 'is-invalid': errors.division_id}">
                                    <option :value="null">-- Include All Divisions --</option>
                                    <option :value="division.id" v-for="division in divisions">{{ division.name }}</option>
                                </select>
                                <small>(Optional). If set, this will filter all data by the division selected.</small>
                                <span v-if="errors.division_id" class="text-danger">
                                    <span v-for="error in errors.division_id">{{ error }}</span>
                                </span>
                            </div>
                        </div>
                        <div class="form-group row">
                            <label :class="{ 'col-lg-2 col-md-3 col-sm-4 col-form-label text-sm-right': true, 'text-danger': errors.min_accept_percent}">Minimum Acceptable %:</label>
                            <div class="col-sm-8">
                                <input type="text" v-model="min_accept_percent" :class="{'form-control': true, 'is-invalid': errors.min_accept_percent}" placeholder="Min. Acceptable %"  style="max-width: 200px;" />
                                <small>(Optional). If set, this will highlight cells red that are less than the Acceptable % rate.</small>
                                <span v-if="errors.min_accept_percent" class="text-danger">
                                    <span v-for="error in errors.min_accept_percent">{{ error }}</span>
                                </span>
                            </div>
                        </div>
                    </div>
                </div>

                <div class="card mb-4">
                    <div class="card-header" :class="{'text-danger': errors.types}">
                        Enabled MIC Types ({{ Object.keys(enabledTypeOptions).length }})
                    </div>
                    <div class="card-body">
                        <div v-if="errors.types" class="text-danger mb-2">
                            <span v-for="error in errors.types">{{ error }}</span>
                        </div>
                        <ul class="list-unstyled">
                            <li v-for="type in types" class="mb-2">
                                <b-form-checkbox v-model="type.enabled" :value="1" :unchecked-value="0" plain>
                                    {{ type.name }}
                                </b-form-checkbox>
                            </li>
                        </ul>
                        <hr/>

                        <div class="text-muted mb-1">Employee Controls (Applies to Public Inspection Forms Only: <span v-for="(form, i) in publicForms"><span v-if="i > 0">, </span>{{ form.name }}</span>)</div>
                        <ul class="list-unstyled">
                            <li>
                                <b-form-checkbox v-model="exclude_temporary_employees" :value="1" :unchecked-value="0" plain>
                                    Exclude Temporary Employees (Employee# Starts with "T")
                                </b-form-checkbox>
                            </li>
                            <li>
                                <b-form-checkbox v-model="exclude_subcontractor_employees" :value="1" :unchecked-value="0" plain>
                                    Exclude Subcontractor Employees (Employee# Starts with "S")
                                </b-form-checkbox>
                            </li>
                        </ul>
                    </div>
                </div>

                <div class="card mb-4">
                    <div class="card-header">
                        Participation Target Goals
                    </div>
                    <div class="card-body">
                        <template v-if="Object.keys(role_filters).length == 0">
                            <h6><i class="fa fa-asterisk text-danger"></i> Target Goals By Type (Required if no Roles Selected Below)</h6>
                            <ul class="list-unstyled">
                                <li v-for="type in enabledTypeOptions" class="mb-2">
                                    <input type="text" v-model="type.goal" placeholder="Enter Target Goal" class="form-control form-control-sm d-inline-block" style="max-width: 150px;" /> / {{ intervals[interval] }}
                                    <span class="text-muted">for</span> {{ type.name }}
                                    <span v-if="errors['types.'+type.id+'.goal']" class="text-danger d-block">
                                        <span v-for="error in errors['types.'+type.id+'.goal']">{{ error }}</span>
                                    </span>
                                </li>
                            </ul>
                            <hr/>
                        </template>

                        <h6>Roles (Optional)</h6>
                        <div class="alert alert-info">
                            You can group the export by the roles assigned to users. If not selected, the list will be a simple user list, not broken down by role.<br>
                            <strong>NOTE:</strong> This will override the Targets above and generate a role-only based report. It will also exclude any employee-created inspection data from public forms.
                        </div>
                        <div class="form-inline">
                            <select v-model="selected_role" class="form-control" v-on:change="updateRoles()">
                                <option :value="null">-- Select a Role --</option>
                                <option :value="role.id" v-for="role in roles">{{ role.name }}</option>
                            </select>
                        </div>
                        <hr/>

                        <ul class="list-unstyled">
                            <li v-for="rf in role_filters" class="mb-3">
                                <button class="btn btn-sm btn-link text-danger" v-on:click="removeRole(rf.id)" title="Remove"><i class="fa fa-remove"></i></button>
                                {{ rf.name }}
                                <ul>
                                    <li v-for="goal in rf.types" class="mb-2">
                                        <input type="text" v-model="goal.goal" placeholder="Enter Target Goal" class="form-control form-control-sm d-inline-block" style="max-width: 150px;" /> / {{ intervals[interval] }}
                                        <span class="text-muted">for</span> {{ goal.name }}
                                        <span v-if="errors['roles.'+rf.id+'.types.'+goal.id+'.goal']" class="text-danger d-block">
                                            <span v-for="error in errors['roles.'+rf.id+'.types.'+goal.id+'.goal']">{{ error }}</span>
                                        </span>
                                    </li>
                                </ul>
                            </li>
                        </ul>
                    </div>
                </div>

                <b-button variant="success" v-on:click="generateExport()" :disabled="saving">
                    <template v-if="saving">
                        Generating <i class="fa fa-circle-notch-o"></i>
                    </template>
                    <template v-else>
                        Generate
                    </template>
                </b-button>
            </div>
        </div>
    </div>
</template>

<script>
    export default {
        data: function() {
            return {
                saving: false,
                start_date: null,
                end_date: null,
                interval: null,
                division_id: null,
                min_accept_percent: null,
                selected_role: null,
                exclude_temporary_employees: 0,
                exclude_subcontractor_employees: 0,
                errors: {},
                intervals: {},
                divisions: [],
                types: {},
                roles: {},
                role_filters: {},
            }
        },

        watch: {
            enabledTypeOptions(newVal, oldVal){
                //Build the Roles
                if(Object.keys(this.role_filters).length === 0)
                {
                    return;
                }

                //Setup
                var keys = Object.keys(this.role_filters);

                //Setup
                for (var i = 0; i < keys.length; i++)
                {
                    //Check if
                    if(typeof this.role_filters[keys[i]] == 'undefined')
                    {
                        continue;
                    }

                    //Setup Types
                    var types = {};

                    //Rebuild
                    for(var key of Object.keys(this.enabledTypeOptions))
                    {
                        //Check if Set
                        if(typeof this.role_filters[keys[i]].types[this.enabledTypeOptions[key].id] != 'undefined')
                        {
                            var type = this.role_filters[keys[i]].types[this.enabledTypeOptions[key].id];
                        }
                        else
                        {
                            var type = {
                                id: this.enabledTypeOptions[key].id,
                                name: this.enabledTypeOptions[key].name,
                                goal: null,
                            };
                        }

                        //Append
                        types[this.enabledTypeOptions[key].id] = type;
                    }

                    //Set them
                    this.role_filters[keys[i]].types = types;
                }
            },
        },

        computed: {
            enabledTypeOptions(){
                //Setup
                var options = {};

                //Loop and Build
                for(var key of Object.keys(this.types))
                {
                    //Check Enabled
                    if(this.types[key].enabled === 1)
                    {
                        options[key] = this.types[key];
                    }
                }

                //Send it
                return options;
            },

            publicForms(){
                //Setup
                var options = [];

                //Loop and Build
                for(var key of Object.keys(this.types))
                {
                    //Check Enabled
                    if(key.indexOf('form_') !== -1)
                    {
                        options.push(this.types[key]);
                    }
                }

                //Send it
                return options;
            },
        },

        methods: {
            //Get the resources needed
            getResources(){
                //Send it
                this.$http.get('/hse/export/advanced')
                    .then(res => {
                        //Setters
                        this.intervals = res.data.intervals;
                        this.divisions = res.data.divisions;
                        this.types = res.data.types;
                        this.roles = res.data.roles;
                    });
            },

            updateRoles(){
                //Check for null
                if(this.selected_role == null)
                {
                    return;
                }

                //Get the role
                var role = this.roles[this.selected_role];

                //Reset here
                this.selected_role = null;

                //Check if it already exists?
                if(this.role_filters[role.id])
                {
                    //Do nothing
                    return;
                }

                //Setup
                var payload = {
                    id: role.id,
                    name: role.name,
                    types: {},
                };

                //Loop and build
                for(var key of Object.keys(this.enabledTypeOptions))
                {
                    //Append
                    payload.types[this.enabledTypeOptions[key].id] = {
                        id: this.enabledTypeOptions[key].id,
                        name: this.enabledTypeOptions[key].name,
                        goal: null,
                    };
                }

                //Push onto the roles
                this.role_filters[role.id] = payload;
            },

            removeRole(id){
                //Helper?
                this.$delete(this.role_filters, id);

                //Remove it
                //delete this.role_filters[id];
            },

            generateExport(){
                //Saving
                this.saving = true;
                this.errors = {};

                //Build the payload
                var payload = {
                    start_date: this.start_date,
                    end_date: this.end_date,
                    interval: this.interval,
                    division_id: this.division_id,
                    min_accept_percent: this.min_accept_percent,
                    types: this.types,
                    roles: this.role_filters,
                    exclude_temporary_employees: this.exclude_temporary_employees,
                    exclude_subcontractor_employees: this.exclude_subcontractor_employees,
                };

                //Send it
                this.$http.post('/hse/export/advanced/alt', payload)
                    .then(res => {
                        //Setters

                        //Open the URL
                        window.open(res.data);
                    })
                    .catch(error => {
                        //Check for error
                        if(error.response.status === 422)
                        {
                            //Display validation errors
                            this.errors = error.response.data;
                        }
                    })
                    .then(() => {
                        //Svaing reset
                        this.saving = false;
                    });
            },
        },

        mounted(){
            //Get the resources
            this.getResources();
        }
    }
</script>
